/**
 * appointments-api — typed wrappers for the Appointments process (APT-14),
 * through the @v0y4ge/api-client `apiRaw` choke point (same seam as
 * customers-api / journeys-api / wf-api).
 *
 * AUTH-FIRST (2026-07-26): per Claude Code's auth wave, every op below states
 * its required capability + scope binding in its doc line, so the family is
 * born classified when it lands in the kit's (currently EMPTY, pre-wired)
 * APPOINTMENTS_OPERATIONS / BOOKING_OPERATIONS fragments — REQ-089 carries the
 * byte-exact shapes. Capability ids here are the APT_BRIDGE coarse set; the
 * canonical 15-cap appointments.* family supersedes them on arrival.
 *
 * TWO TIERS:
 *  • DECLARED admin family (/v1/admin/appointments/*) — pending kit-side; the
 *    preview harness serves it from CD preview seeds (appointments-preview-
 *    seeds.js, `__source: "cd-preview-seed"`), surfaced via <DataSourceStrip>.
 *    The concierge's REQ-040 pair (/v1/admin/queues/appointments/*) becomes an
 *    alias of this canonical family.
 *  • SHIPPED public fns (lookup/cancel/reschedule/searchSlots) — GM15's typed
 *    client already exports them; ManageBooking wires them directly. While kit
 *    mock families are dist-gated in preview, the same seed layer answers
 *    their routes.
 * No fixture data lives here — records come back from the ops.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { listAppointmentMessageTemplates, saveAppointmentMessageTemplate } from "@v0y4ge/api-client";
import type { AppointmentMessageTemplate, SaveAppointmentMessageTemplateInput } from "@v0y4ge/api-contracts";
import { reportOpSource } from "../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });
const json = (body: unknown, method = "POST"): RequestInit => ({ method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
/** Query string builder. An array value repeats the key (`staffId=a&staffId=b`);
 *  null / undefined / empty values are dropped. */
const q = (params: Record<string, string | string[] | undefined | null>): string => {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    const values: Array<string | undefined | null> = Array.isArray(v) ? v : [v];
    for (const item of values) {
      if (item == null || item === "") continue;
      parts.push(`${k}=${encodeURIComponent(String(item))}`);
    }
  }
  return parts.length ? `?${parts.join("&")}` : "";
};

export type ApptStatus = "booked" | "confirmed" | "checked-in" | "served" | "no-show" | "cancelled" | "rescheduled";
export type ApptChannel = "inPerson" | "phone" | "video";
export interface Appointment {
  id: string; ref: string; locationId: string; date: string; start: string; end: string;
  service: string; serviceId?: string; staffId: string | null; staffName: string | null;
  customer: { name: string; email?: string; phone?: string };
  channel: ApptChannel; status: ApptStatus; notes?: string; createdAt?: string;
  /** Additional customer attendees on the same booking (JRNI §6.1). */
  attendees?: ApptAttendee[];
  /** Secondary staff serving alongside staffId (JRNI §6.3). */
  additionalStaff?: ApptPartyRef[];
  /** Resources this booking consumes — rooms, suites, equipment (JRNI §3/§6.3). */
  resources?: ApptPartyRef[];
  /** Where the booking came from — 'journey' | 'concierge' | 'staff' | 'import'. */
  origin?: string;
  /** Booked minutes (the slot length, excluding pre/post blocks). */
  durationMin?: number;
  /** Resolved location name, so a record page need not re-read the location list. */
  locationName?: string;
  /** How the booking was taken — website | mobile | phone | email | staff | queue | event. */
  bookedVia?: string | null;
  /** The queue or event the booking came out of, when it came from one. */
  bookedFrom?: string | null;
  /** ISO datetime the booking was made. */
  bookedAt?: string;
  /** Answers the customer gave to the service's booking questions (APT-15). */
  answers?: ApptAnswer[];
  /** Marketing attribution captured on a self-service booking (APT-15). */
  attribution?: ApptAttribution;
  /** What the customer's browser reported at booking time. Self-service only. */
  client?: ApptClient;
  /** Charge on the booking, when the service is paid. */
  price?: ApptPrice;
  /** Part payments recorded against the booking. */
  payments?: ApptPayment[];
}
/** Price block. `paid` is derived from the payments, never stored twice. */
export interface ApptPrice { amount: number; currency: string; status?: string; couponCode?: string | null; discount?: number | null }
export interface ApptPayment { id: string; amount: number; date: string; method: string; paid: boolean }
/** Booking client — device/browser provenance behind a self-service booking. */
export interface ApptClient {
  device?: string | null; browser?: string | null; os?: string | null;
  engine?: string | null; ip?: string | null; userAgent?: string | null;
}
/** One answered booking question. `qid` references the tenant's question catalogue. */
export interface ApptAnswer { qid?: string; question: string; answer: string; }
/** UTM + landing context for a self-service booking. Absent on staff-taken bookings. */
export interface ApptAttribution {
  /** utm_id — the campaign/ad id the click carried. */
  id?: string | null;
  source?: string | null; medium?: string | null; campaign?: string | null;
  content?: string | null; term?: string | null;
  referrer?: string | null; landing?: string | null; device?: string | null;
}
export interface ApptLocationRef { id: string; name: string; }
export interface ApptDayCount { date: string; total: number; }
export interface ApptServiceRef { id: string; name: string; duration: number; channels: ApptChannel[]; }
export interface ApptSlot { start: string; end: string; staffId: string; staffName: string; }
/** JRNI §6 wire-in (2026-08-05): one appointment can carry extra customer
 * attendees, secondary staff, and the resources it consumes. Additive — every
 * field optional, absent on legacy rows. REQ-130 carries the declared-family
 * shape ask. */
export interface ApptAttendee { name: string; email?: string; phone?: string; }
export interface ApptPartyRef { id: string; name: string; }

/* ── Book read: one day (legacy) or one inclusive range (2026-09-15) ──
 * The day book used to walk its horizon one `date` read per day per location.
 * The same GET now answers an inclusive dateFrom/dateTo range (max 90 days)
 * with the filters applied server-side, a filtered `total`, facets computed
 * over the UNFILTERED scope (range + location), and an unfiltered `summary`.
 * A `date`-only call keeps its old meaning and shape. */

/** `locationId` value that scopes the read to every location. */
export const APPT_ALL_LOCATIONS = "all";
/** Inclusive day cap on a dateFrom/dateTo read. */
export const APPT_RANGE_MAX_DAYS = 90;

export type ApptFacetId = "status" | "staff" | "service" | "channel" | "via" | "campaign" | "question" | "location";
/** One facet value. Staff and service facets carry the ID as `value` and the
 *  human name as `label`; the other facets carry the raw value (status code,
 *  channel code, via, campaign, question text, location id). */
export interface ApptFacetOption { value: string; label: string; count: number }
export interface ApptSummary { total: number; checkedIn: number; served: number; noShow: number }

export interface ListAppointmentsParams {
  /** Legacy one-day selector. Cannot be combined with dateFrom/dateTo. */
  date?: string;
  /** Inclusive range start (YYYY-MM-DD). Requires dateTo. */
  dateFrom?: string;
  /** Inclusive range end (YYYY-MM-DD). At most APPT_RANGE_MAX_DAYS after dateFrom, inclusive. */
  dateTo?: string;
  /** Omitted / null → the caller's default location; APPT_ALL_LOCATIONS → every location; otherwise a location (company) id. */
  locationId?: string | null;
  /** Repeated values are ORed within a filter; filters are ANDed together. */
  staffId?: string | string[] | null;
  serviceId?: string | string[] | null;
  status?: string | string[] | null;
  channel?: string | string[] | null;
  via?: string | string[] | null;
  campaign?: string | string[] | null;
  question?: string | string[] | null;
  /** Free-text search. */
  q?: string | null;
}

export interface AppointmentsListResponse {
  date: string;
  locationId: string;
  locations: ApptLocationRef[];
  week: ApptDayCount[];
  appointments: Appointment[];
  /** Present on a range read. */
  dateFrom?: string;
  dateTo?: string;
  /** Appointments matching every filter (range read). */
  total?: number;
  /** Facet values and counts over the unfiltered scope (range read). */
  facets?: Partial<Record<ApptFacetId, ApptFacetOption[]>>;
  /** Status totals over the unfiltered scope (range read). */
  summary?: ApptSummary;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayNumber = (iso: string): number | null => {
  if (!ISO_DAY.test(iso)) return null;
  const ms = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== iso) return null;
  return Math.round(ms / 86_400_000);
};
/** Inclusive day count of a range, or null when either end is not a real ISO day. */
export const apptRangeDays = (dateFrom: string, dateTo: string): number | null => {
  const from = dayNumber(dateFrom);
  const to = dayNumber(dateTo);
  return from == null || to == null ? null : to - from + 1;
};
const listParamsError = (p: ListAppointmentsParams): string | null => {
  const hasRange = !!p.dateFrom || !!p.dateTo;
  if (p.date) return hasRange ? "date cannot be combined with dateFrom/dateTo." : null;
  if (!p.dateFrom || !p.dateTo) return "date or dateFrom and dateTo are required.";
  const days = apptRangeDays(p.dateFrom, p.dateTo);
  if (days == null) return "dateFrom and dateTo must be ISO days (YYYY-MM-DD).";
  if (days < 1) return "dateFrom must not be after dateTo.";
  if (days > APPT_RANGE_MAX_DAYS) return `the range covers ${days} days; the maximum is ${APPT_RANGE_MAX_DAYS}.`;
  return null;
};

/** cap: appointments.read · scope: location — the day book read (one day, or one inclusive range). */
export const listAppointments = (params: ListAppointmentsParams): Promise<AppointmentsListResponse> => {
  const invalid = listParamsError(params);
  if (invalid) return Promise.reject(new RangeError(`listAppointments: ${invalid}`));
  const search = params.q?.trim();
  return tag("listAppointments", apiRaw<AppointmentsListResponse>(
    `/v1/admin/appointments${q({
      date: params.date || undefined,
      dateFrom: params.date ? undefined : params.dateFrom,
      dateTo: params.date ? undefined : params.dateTo,
      locationId: params.locationId ?? undefined,
      staffId: params.staffId ?? undefined,
      serviceId: params.serviceId ?? undefined,
      status: params.status ?? undefined,
      channel: params.channel ?? undefined,
      via: params.via ?? undefined,
      campaign: params.campaign ?? undefined,
      question: params.question ?? undefined,
      q: search || undefined,
    })}`, undefined, 'listAppointments'));
};

/** cap: appointments.read · scope: location. */
export const getAppointment = (id: string) =>
  tag("getAppointment", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}`, undefined, 'getAppointment'));

/** cap: appointments.read · scope: location — service catalog for the booking sheet. */
export const listAppointmentServices = (locationId?: string | null) =>
  tag("listAppointmentServices", apiRaw<{ services: ApptServiceRef[] }>(`/v1/admin/appointments/services${q({ locationId: locationId ?? undefined })}`, undefined, 'listAppointmentServices'));

/** cap: appointments.read · scope: location — canonical slot grid (REQ-040's
 * listApptSlots migrates onto this path; the queues alias stays until then). */
export const listApptSlots = (params: { service: string; date: string; locationId?: string | null; staffId?: string | null }) =>
  tag("listApptSlots", apiRaw<{ date: string; service: string; slots: ApptSlot[] }>(
    `/v1/admin/appointments/slots${q({ service: params.service, date: params.date, locationId: params.locationId ?? undefined, staffId: params.staffId ?? undefined })}`, undefined, 'listApptSlots'));

export interface BookApptInput {
  service: string; serviceId?: string; date: string; start: string; end: string;
  staffId?: string | null; staffName?: string | null; locationId?: string | null;
  channel?: ApptChannel;
  customer: { name: string; email?: string; phone?: string };
  attendees?: ApptAttendee[];
  additionalStaff?: ApptPartyRef[];
  resources?: ApptPartyRef[];
  notes?: string; origin?: string;
}
/** cap: appointments.book · scope: location — staff-side booking (shared sheet). */
export const bookAppointment = (input: BookApptInput) =>
  tag("bookAppointment", apiRaw<Appointment>("/v1/admin/appointments", json(input), 'bookAppointment'));

/** cap: appointments.book · scope: location. */
export const rescheduleAppointment = (id: string, input: { date: string; start: string; end: string; staffId?: string | null; staffName?: string | null; reason?: string }) =>
  tag("rescheduleAppointment", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/reschedule`, json(input), 'rescheduleAppointment'));

/** cap: appointments.book · scope: location. */
export const cancelAppointment = (id: string, reason?: string) =>
  tag("cancelAppointment", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/cancel`, json(reason ? { reason } : {}), 'cancelAppointment'));

/** cap: appointments.checkin · scope: location — kit schema CheckInBookingRequest
 * (source staff|kiosk|qr|api) already shipped in contracts/src/booking/checkin.ts. */
export const checkInAppointment = (id: string) =>
  tag("checkInAppointment", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/checkin`, json({ source: "staff", notify: false }), 'checkInAppointment'));

/** cap: appointments.checkin · scope: location. */
export const markAppointmentNoShow = (id: string) =>
  tag("markAppointmentNoShow", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/no-show`, json({}), 'markAppointmentNoShow'));

/* ── Price & payments (APT-16, 2026-08-06) ── */
/** cap: appointments.book · scope: location — set or clear the charge. */
export const setAppointmentPrice = (id: string, price: { amount: number; currency: string; couponCode?: string | null; discount?: number | null }) =>
  tag("setAppointmentPrice", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/price`, json(price, "PUT"), 'setAppointmentPrice'));
/** cap: appointments.book · scope: location — record a part payment. */
export const addAppointmentPayment = (id: string, p: { amount: number; date: string; method: string; paid: boolean }) =>
  tag("addAppointmentPayment", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/payments`, json(p), 'addAppointmentPayment'));
/** cap: appointments.book · scope: location. */
export const updateAppointmentPayment = (id: string, paymentId: string, patch: Partial<ApptPayment>) =>
  tag("updateAppointmentPayment", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/payments/${encodeURIComponent(paymentId)}`, json(patch, "PATCH"), 'updateAppointmentPayment'));
/** cap: appointments.book · scope: location. */
export const deleteAppointmentPayment = (id: string, paymentId: string) =>
  tag("deleteAppointmentPayment", apiRaw<Appointment>(`/v1/admin/appointments/${encodeURIComponent(id)}/payments/${encodeURIComponent(paymentId)}`, { method: "DELETE" }, 'deleteAppointmentPayment'));

/* ── Block list (APT-17, 2026-08-06) — an identifier a tenant refuses bookings
   from. One family for all four kinds so "who is blocked here" is one read. ── */
export type BlockKind = "customer" | "email" | "phone" | "ip";
export interface BlockEntry { id: string; kind: BlockKind; value: string; label?: string; reason?: string; createdAt?: string; createdBy?: string }
/** cap: appointments.manage · scope: org. */
export const listBlocks = () =>
  tag("listBlocks", apiRaw<{ entries: BlockEntry[] }>("/v1/admin/blocklist", undefined, 'listBlocks'));
/** cap: appointments.manage · scope: org. */
export const addBlock = (b: { kind: BlockKind; value: string; label?: string; reason?: string }) =>
  tag("addBlock", apiRaw<BlockEntry>("/v1/admin/blocklist", json(b), 'addBlock'));
/** cap: appointments.manage · scope: org. */
export const removeBlock = (id: string) =>
  tag("removeBlock", apiRaw<{ ok: boolean }>(`/v1/admin/blocklist/${encodeURIComponent(id)}`, { method: "DELETE" }, 'removeBlock'));

/* ── Reporting (APT-10) ── */
export interface ApptReportRow { id: string; name: string; booked: number; kept: number; noShow: number; }
export interface AppointmentsReport {
  range: string; locationId: string; locations: ApptLocationRef[];
  kpis: { booked: number; kept: number; noShow: number; cancelled: number; rescheduled: number; keptRate: number; noShowRate: number };
  funnel: { stage: string; count: number }[];
  byService: ApptReportRow[];
  byStaff: ApptReportRow[];
  byOrigin: { origin: string; count: number }[];
}
/** cap: appointments.read · scope: location · reports family (REQ-089). */
export const getAppointmentsReport = (params: { range: string; locationId?: string | null }) =>
  tag("getAppointmentsReport", apiRaw<AppointmentsReport>(`/v1/admin/appointments/report${q({ range: params.range, locationId: params.locationId ?? undefined })}`, undefined, 'getAppointmentsReport'));

/* ── Booking comms templates (APT-08) — RESTORED 2026-09-02 (operator).
 *
 * This facade went out of the tree with `messaging/ApptComms.tsx` between
 * 2026-08-25 and 2026-08-31, unrecorded (see CHANGES-FROM-NOW.md). Restored on
 * the TYPED ops rather than the raw `apiRaw` calls it used to make: the three
 * appointment-comms operations exist now (`api-client/src/appointment-comms.ts`,
 * added by messaging mid-tier v4), and the lane is live — per CC 2026-09-02 it
 * is today the only path that actually sends a booking confirmation.
 *
 * The CD-facing names stay `ApptEmailTemplate` / `ApptSmsTemplate` so the
 * restored screen is verbatim. Since #1654 they alias the two MEMBERS of the
 * channel-discriminated union rather than one flat row: the email member types
 * `subject: string` (so the screen's subject field and preview typecheck), the
 * sms member refuses `subject`/`preheader`. `ApptMessageTemplate` is the union
 * itself — what a write takes, since an intersection of two members that
 * disagree about `subject` is not a type anything can hold. ── */
export type { AppointmentMessageEmailTemplate as ApptEmailTemplate } from "@v0y4ge/api-contracts";
export type { AppointmentMessageSmsTemplate as ApptSmsTemplate } from "@v0y4ge/api-contracts";
export type { AppointmentMessageTemplate as ApptMessageTemplate } from "@v0y4ge/api-contracts";

/** cap: appointments.manage · scope: org — the template list, grouped by channel. */
export const listApptMessageTemplates = () =>
  tag("listApptMessageTemplates", listAppointmentMessageTemplates());

/** cap: appointments.manage · scope: org. An existing id updates; the server
 *  validates `trigger` against its registered catalog and 422s a typo rather
 *  than persisting a template that would silently never fire. */
export const saveApptMessageTemplate = (
  channel: "email" | "sms",
  tpl: AppointmentMessageTemplate,
) => tag("saveApptMessageTemplate", saveAppointmentMessageTemplate(channel, tpl.id, tpl as SaveAppointmentMessageTemplateInput));

/* ── Public self-service (APT-09) — SHIPPED GM15 client fns, re-exported so
 * screens import one appointments facade. cap: none (public, ref-scoped). ── */
export { lookupBooking, cancelBooking, rescheduleBooking } from "@v0y4ge/api-client";
export { searchSlots } from "@v0y4ge/api-client";
